import {
  Box,
  ButtonGroup,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FrameworkAgreementType, type SourcingEventV2Dto } from "../../../../../autogen/bff-api";
import { useSaveField } from "../hooks/useSaveField";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { FRAMEWORK_AGREEMENT_OPTIONS, isFrameworkEnabled } from "../utils/publicProcurementOptions";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
}

export const FrameworkAgreementField = ({ eventId, event }: Props) => {
  const { t } = useTranslation();
  const { saveField, isSaving } = useSaveField(eventId);

  const [enabled, setEnabled] = useState(isFrameworkEnabled(event.publicContext.frameworkAgreement));
  const [type, setType] = useState<FrameworkAgreementType | null>(
    isFrameworkEnabled(event.publicContext.frameworkAgreement) ? (event.publicContext.frameworkAgreement ?? null) : null
  );

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    const defaultType = FRAMEWORK_AGREEMENT_OPTIONS[0].value;
    const newType = checked ? (type ?? defaultType) : null;
    if (checked && !type) setType(defaultType);
    if (!checked) setType(null);
    saveField({ frameworkAgreement: checked ? newType : FrameworkAgreementType.None });
  };

  const handleTypeChange = (value: string) => {
    const enumValue = value as FrameworkAgreementType;
    setType(enumValue);
    saveField({ frameworkAgreement: enumValue });
  };

  return (
    <FormControl mb="8">
      <Flex align="center" gap="4" mb={enabled ? "4" : "0"}>
        <FormLabel mb="0">{t("Framework agreement")}</FormLabel>
        <ButtonGroup isAttached size="sm">
          <Button
            colorScheme={enabled ? "teal" : "gray"}
            variant={enabled ? "solid" : "outline"}
            onClick={() => handleToggle(true)}
          >
            {t("Yes")}
          </Button>
          <Button
            colorScheme={!enabled ? "teal" : "gray"}
            variant={!enabled ? "solid" : "outline"}
            onClick={() => handleToggle(false)}
          >
            {t("No")}
          </Button>
        </ButtonGroup>
        {isSaving("frameworkAgreement") && <Spinner size="xs" />}
      </Flex>

      {enabled && (
        <Box borderWidth="1px" borderRadius="lg" borderColor="smBorder" p="5">
          <Text fontWeight="semibold" mb="4">
            {t("Call-off mechanism")}
          </Text>
          <RadioOptionGroup
            options={FRAMEWORK_AGREEMENT_OPTIONS}
            value={type ?? ""}
            onChange={handleTypeChange}
          />
        </Box>
      )}
    </FormControl>
  );
};
